import React, { useState } from 'react';
import {
  Database,
  Package,
  DollarSign,
  BarChart3,
  ArrowRight,
  Factory,
  Users,
  Truck,
  ShieldCheck,
  CheckCircle2,
  Terminal,
  Activity,
  Layers,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';

const ERPSolution = () => {
  const [activeModule, setActiveModule] = useState('finance');

  const erpPillars = [
    {
      icon: DollarSign,
      title: 'Multi-Entity Finance & General Ledger',
      badge: 'Real-Time Ledger',
      color: 'from-cyan-500 to-blue-600',
      description: 'Automated General Ledger (GL), Accounts Payable (AP), Accounts Receivable (AR), multi-currency conversions, and tax compliance audits.',
      bullets: ['Automated GL, AP & AR Ledger', 'Multi-Currency Conversion Engine', 'Audit-Ready Tax Compliance Reports']
    },
    {
      icon: Package,
      title: 'Warehouse & Barcode Inventory',
      badge: 'Multi-Warehouse',
      color: 'from-purple-500 to-indigo-600',
      description: 'Real-time batch & serial tracking, multi-warehouse stock synchronization, FIFO/LIFO valuation, and automated reorder point alerts.',
      bullets: ['Barcode & RFID Serial Tracking', 'Multi-Warehouse Stock Sync', 'Automated Low-Stock Reorder Triggers']
    },
    {
      icon: Truck,
      title: 'Supply Chain & Procurement',
      badge: 'Vendor Portal',
      color: 'from-orange-500 to-amber-500',
      description: 'Vendor bidding management, purchase order approvals, supplier performance scorecards, and automated logistics tracking.',
      bullets: ['Vendor Portal & PO Approvals', 'Supplier SLA Scorecards', 'Real-Time Inbound Freight Tracking']
    },
    {
      icon: Factory,
      title: 'Manufacturing & MRP II Engine',
      badge: 'Shop Floor',
      color: 'from-emerald-500 to-teal-600',
      description: 'Bill of Materials (BOM) management, work center capacity scheduling, shop floor routing, and material scrap reduction.',
      bullets: ['Bill of Materials (BOM) Specs', 'Work Center Capacity Scheduling', 'Shop Floor Scrap Minimization']
    },
    {
      icon: Users,
      title: 'Human Capital & Payroll Engine',
      badge: 'HR & Payroll',
      color: 'from-pink-500 to-rose-600',
      description: 'Integrated employee self-service portals, biometric attendance sync, automated tax withholdings, and direct deposit payroll dispatch.',
      bullets: ['Biometric Attendance Sync', 'Tax Withholding & Compliance', 'Direct Deposit Payroll Dispatch']
    },
    {
      icon: BarChart3,
      title: 'Executive BI Operations Center',
      badge: 'CFO Telemetry',
      color: 'from-blue-500 to-cyan-600',
      description: 'Real-time CFO dashboards monitoring operating margins, working capital, inventory turnover ratio, and custom executive reports.',
      bullets: ['Real-Time Cash Flow Telemetry', 'Inventory Turnover Analytics', 'Executive Dashboard Export']
    }
  ];

  const moduleSpecs = {
    finance: {
      name: 'Financial & Accounting Suite',
      desc: 'Close month-end books 4x faster with automated bank reconciliation, asset depreciation schedules, and multi-entity consolidation.',
      specs: ['Automated Bank Reconciliation', 'Asset Depreciation Schedules', 'Multi-Company Consolidated Ledger', 'Instant Balance Sheet Export']
    },
    supply: {
      name: 'Supply Chain & Inventory Suite',
      desc: 'Eliminate stockouts and overstock costs with AI-driven demand forecasting, land cost calculations, and barcode scanning.',
      specs: ['Demand Forecasting Algorithms', 'Landed Cost Calculation Engine', 'Barcode / QR Mobile Scanner App', 'Stock Transfer Approval Routing']
    },
    manufacturing: {
      name: 'Manufacturing & Production Suite',
      desc: 'Optimize shop floor throughput with real-time machine utilization tracking, work order generation, and quality inspection gates.',
      specs: ['Real-Time Machine Telemetry', 'Automated Work Order Dispatch', 'Quality Control Inspection Gates', 'Standard vs Actual Cost Variance']
    }
  };

  const erpSteps = [
    { step: '01', title: 'Process Discovery & Gap Analysis', desc: 'Mapping department workflows across finance, supply chain, and HR to define technical ERP specifications.' },
    { step: '02', title: 'Master Data Cleaning & Migration', desc: 'De-duplicating and migrating legacy GL entries, product SKUs, and customer profiles into centralized databases.' },
    { step: '03', title: 'Custom Module & Workflow Configuration', desc: 'Engineering custom approval rules, tax rates, inventory valuations, and API integration hooks.' },
    { step: '04', title: 'User Acceptance Testing (UAT) & Training', desc: 'Conducting department-by-department simulation trials and staff role-based training.' },
    { step: '05', title: 'Go-Live Release & 24/7 SLA Support', desc: 'Executing cutover release, monitoring real-time transaction ledgers, and providing 24/7 technical support.' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20 text-gray-100">

      {/* Hero Showcase Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 via-slate-950 to-gray-950 relative overflow-hidden border-b border-gray-800/80">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
                <Database className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                  Next-Gen Cloud ERP Suite
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Integrated Enterprise Resource <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Planning (ERP)</span>
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                Unify finance, supply chain, inventory, procurement, and human capital into a single real-time enterprise command suite with automated multi-currency accounting.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Schedule ERP Demo
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#erp-pillars">
                  <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Explore Capabilities
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live ERP Telemetry Console */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Console Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-cyan-400 animate-ping" />
                    <span className="text-sm font-mono text-cyan-400 font-bold">ERP COMMAND: LIVE</span>
                  </div>
                  <span className="text-[11px] font-mono text-gray-400">LEDGER SYNC v6.0</span>
                </div>

                {/* Telemetry Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Monthly Ledger</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">$12.4M</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Active SKUs</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">45,210</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Supply SLA</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">99.8%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Warehouses</div>
                    <div className="text-2xl font-extrabold text-blue-400 font-mono">12 SITES</div>
                  </div>
                </div>

                {/* Terminal Line */}
                <div className="bg-gray-950 p-3 rounded-xl border border-gray-800 font-mono text-sm text-gray-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-cyan-400" />
                  <span>erp_ledger.reconcile_accounts(#BATCH-9402)</span>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Performance Metrics Banner */}
      <section className="py-10 bg-gray-900/80 border-y border-gray-800/80">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-cyan-400 mb-1">40%+</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Operating Cost Reduction</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-1">99.9%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Inventory Accuracy SLA</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-blue-400 mb-1">100%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Multi-Ledger Sync</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-400 mb-1">SOC 2</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Compliance Certified</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars Grid */}
      <section id="erp-pillars" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Full-Spectrum ERP Pillars
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Enterprise ERP <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Capabilities</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From multi-entity finance accounting to barcode warehouse management and MRP manufacturing schedules.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {erpPillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-cyan-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <pillar.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {pillar.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive ERP Module Selector */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Explore Enterprise ERP Modules
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8">
              Modular architecture configured specifically for your financial accounting, inventory, or manufacturing requirements.
            </p>

            <div className="inline-flex p-1 bg-gray-900 border border-gray-800 rounded-xl space-x-2">
              <button
                onClick={() => setActiveModule('finance')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'finance'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Financial & Ledger
              </button>
              <button
                onClick={() => setActiveModule('supply')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'supply'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Supply Chain & Inventory
              </button>
              <button
                onClick={() => setActiveModule('manufacturing')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'manufacturing'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Manufacturing & MRP
              </button>
            </div>
          </div>

          <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-3">
              {moduleSpecs[activeModule].name}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-8">
              {moduleSpecs[activeModule].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {moduleSpecs[activeModule].specs.map((spec, idx) => (
                <div key={idx} className="bg-gray-950/80 border border-gray-800 rounded-xl p-4 flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-200">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 5-Step ERP Implementation Roadmap */}
      <section className="py-24 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              5-Step Enterprise ERP Deployment Roadmap
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              From process discovery and data cleaning to UAT training and live production cutover.
            </p>
          </div>

          <div className="space-y-6">
            {erpSteps.map((proc, idx) => (
              <div key={idx} className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6 hover:border-cyan-500/50 transition-all">
                <span className="text-3xl font-black text-cyan-400 font-mono flex-shrink-0">{proc.step}</span>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">{proc.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{proc.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Full-Width Executive Consultation CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 via-gray-950 to-slate-900 relative overflow-hidden border-t border-gray-800/80 w-full">
        <div className="container mx-auto px-6 relative z-10 max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-900 via-gray-800 to-slate-900 border border-cyan-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Ready to Transform Your Enterprise Operations with ERP?
            </h2>
            <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
              Schedule an executive consultation with our ERP system architects to plan your multi-ledger accounting, inventory, and supply chain migration.
            </p>
            <a href="/contact">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg">
                Schedule Executive ERP Architecture Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ERPSolution;